import { useCallback } from 'react'

import { useLazyQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const REACH_LIMIT_ERROR =
  '[Comments] You have reached the maximum number of suggestions for your plan'

export type CommentSuggestionsQueryParams = {
  commentId: string
  regenerate?: boolean
}

export const getCommentSuggestionsQuery = graphql(`
  query commentSuggestions($input: CommentSuggestionsInput!) {
    commentSuggestions(input: $input) {
      suggestions {
        text
        source
      }
    }
  }
`)

type Suggestion = NonNullable<
  NonNullable<
    ResultOf<typeof getCommentSuggestionsQuery>['commentSuggestions']
  >['suggestions']
>[number]

type UseCommentSuggestionsReturn = {
  fetchCommentSuggestions: (params: CommentSuggestionsQueryParams) => Promise<{
    data: Suggestion[] | undefined
    error: string | null
  }>
}

/**
 * Hook to fetch AI-generated comment suggestions for given comments.
 *
 * @returns {UseCommentSuggestionsReturn} An object containing the fetchCommentSuggestions function
 *
 * @example
 * const { fetchCommentSuggestions } = useCommentSuggestions()
 * const { data, loading, error } = await fetchCommentSuggestions({
 *   commentId: '123456789012123456789012'
 *   regenerate: true
 * })
 */
export function useCommentSuggestions(): UseCommentSuggestionsReturn {
  const [fetchSuggestions] = useLazyQuery(getCommentSuggestionsQuery, {
    fetchPolicy: 'network-only',
  })

  const fetchCommentSuggestions = useCallback(
    async (params: CommentSuggestionsQueryParams) => {
      const { commentId } = params

      if (!commentId) {
        return {
          data: undefined,
          error: 'Comment Id is required',
        }
      }
      try {
        const { data, error } = await fetchSuggestions({
          variables: {
            input: {
              commentId,
              regenerate: params.regenerate,
            },
          },
        })

        if (error) {
          return {
            data: undefined,
            error: error.message.includes(REACH_LIMIT_ERROR)
              ? 'reachLimit'
              : null,
          }
        }

        return {
          data: data?.commentSuggestions?.suggestions,
          error: null,
        }
      } catch (error) {
        // Local diagnostic for a non-blocking transient failure (rate limit, network);
        // intentionally not routed to Sentry to avoid noise.
        // eslint-disable-next-line no-console
        console.error('Error fetching comment suggestions:', error)

        /* We are failing silently here because we don't want to break the app if the quick replies fail to load */
        return {
          data: undefined,
          error: 'Error fetching suggestions',
        }
      }
    },
    [fetchSuggestions],
  )

  return { fetchCommentSuggestions }
}
